import React, { useState, useCallback, useEffect } from "react";
import Cookies from "js-cookie";
import {
    Loader2, AlertCircle, ChevronLeft, ChevronRight, Mail, Hash, CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { environment } from "@/env/env.prod";

// ─────────────────────────────────────────────────────────────────────────
// Department roster — faculty-only, paginated, backed by
// Get_Department_Members. Departments previously had no member list at
// all (membership only ever lived as a field on each user's own profile,
// with no reverse lookup) — this is read-only viewing; joining/leaving
// still happens the existing way (Join_Department, faculty reset).
// ─────────────────────────────────────────────────────────────────────────

type Member = {
    uid: string;
    name: string;
    eid: string;
    email: string;
    role: string;
    profilePic: string | null;
    joinedAt: number | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    departmentName: string | null; // raw, underscored — matches Join_Department's identifier
    departmentDisplayName: string;
};

const PAGE_SIZE = 20;

function initialsFor(name: string) {
    return (name || "?").split(" ").map(n => n[ 0 ]).join("").substring(0, 2).toUpperCase();
}

function formatJoinedAt(ts: number | null) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function DepartmentRoster({ open, onClose, departmentName, departmentDisplayName }: Props) {
    const [ roleFilter, setRoleFilter ] = useState<"student" | "teacher">("student");
    const [ members, setMembers ] = useState<Member[]>([]);
    const [ page, setPage ] = useState(1);
    const [ total, setTotal ] = useState(0);
    const [ hasMore, setHasMore ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);

    const fetchPage = useCallback(async (targetPage: number, role: "student" | "teacher") => {
        if (!departmentName) return;
        setLoading(true);
        setError(null);
        try {
            const authToken = Cookies.get("authToken");
            const parent = Cookies.get("ins_code");
            const res = await fetch(`${environment.BASE_URL}/Get_Department_Members`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ AuthToken: authToken, parent, departmentName, role, page: targetPage, limit: PAGE_SIZE }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Failed to load department roster");
            setMembers(data.members || []);
            setTotal(data.total || 0);
            setHasMore(!!data.hasMore);
            setPage(targetPage);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [ departmentName ]);

    useEffect(() => {
        if (!open || !departmentName) return;
        setRoleFilter("student");
        fetchPage(1, "student");
    }, [ open, departmentName, fetchPage ]);

    const switchRole = (role: "student" | "teacher") => {
        setRoleFilter(role);
        fetchPage(1, role);
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden border-border/60 shadow-2xl">
                <div className="p-6 border-b border-border/40 bg-muted/5 shrink-0">
                    <DialogTitle className="text-xl font-bold tracking-tight">{departmentDisplayName}</DialogTitle>
                    <p className="text-[10px] text-muted-foreground mt-1 uppercase font-black tracking-widest">
                        {total} {roleFilter === "student" ? "Student" : "Teacher"}{total === 1 ? "" : "s"}
                    </p>

                    <div className="mt-4 flex gap-1 bg-muted rounded-lg p-1 w-fit">
                        <Button
                            size="sm"
                            variant={roleFilter === "student" ? "default" : "ghost"}
                            className="h-7 text-xs"
                            onClick={() => switchRole("student")}
                        >
                            Students
                        </Button>
                        <Button
                            size="sm"
                            variant={roleFilter === "teacher" ? "default" : "ghost"}
                            className="h-7 text-xs"
                            onClick={() => switchRole("teacher")}
                        >
                            Teachers
                        </Button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-[oklch(0.99_0.005_90)]">
                    {loading ? (
                        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-2">
                            <AlertCircle className="h-6 w-6 text-red-400" />
                            <p className="text-sm text-muted-foreground">{error}</p>
                        </div>
                    ) : members.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                            No {roleFilter === "student" ? "students" : "teachers"} in this department yet.
                        </p>
                    ) : (
                        members.map((m) => (
                            <div key={m.uid} className="flex items-center justify-between p-4 rounded-xl border border-border/40 bg-card hover:border-primary/40 hover:shadow-md transition-all group">
                                <div className="flex items-center gap-4 min-w-0">
                                    <Avatar className="h-11 w-11 shrink-0 border border-border/40">
                                        <AvatarImage src={m.profilePic ?? ""} />
                                        <AvatarFallback className="bg-primary/5 text-primary/70 font-bold">{initialsFor(m.name)}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="text-sm font-bold tracking-tight truncate">{m.name || "—"}</span>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-[11px] text-muted-foreground font-medium">
                                            <span className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 text-primary/40 shrink-0" /> {m.email || "—"}</span>
                                            <span className="flex items-center gap-1.5 shrink-0"><Hash className="h-3 w-3 text-primary/40" /> {m.eid || "—"}</span>
                                            <span className="flex items-center gap-1.5 shrink-0"><CalendarDays className="h-3 w-3 text-primary/40" /> Joined {formatJoinedAt(m.joinedAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="p-4 border-t border-border/40 flex items-center justify-between bg-card shrink-0">
                    <p className="text-[11px] text-muted-foreground uppercase font-bold tracking-tighter">Page {page} of {totalPages}</p>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-8 text-[11px] font-bold gap-1" disabled={page <= 1 || loading} onClick={() => fetchPage(page - 1, roleFilter)}>
                            <ChevronLeft className="h-3.5 w-3.5" /> Prev
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-[11px] font-bold gap-1" disabled={!hasMore || loading} onClick={() => fetchPage(page + 1, roleFilter)}>
                            Next <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                        <DialogClose asChild>
                            <Button variant="ghost" className="text-xs font-bold px-6 uppercase tracking-widest ml-2">Close</Button>
                        </DialogClose>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
